import React, { useState, useRef } from 'react';
import {
  Card,
  Button,
  Upload,
  Table,
  Tag,
  Space,
  message,
  Progress,
  Steps,
  Tooltip,
  Modal,
  Descriptions,
  Badge,
  Row,
  Col,
  Statistic,
  Alert,
  Collapse,
  Input,
} from 'antd';
import {
  UploadOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SyncOutlined,
  LinkOutlined,
  EditOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  CloudUploadOutlined,
  FileSyncOutlined,
  SafetyCertificateOutlined,
  SwapOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

const { Step } = Steps;
const { Panel } = Collapse;
const { TextArea } = Input;

// 处理环节类型
type ProcessStep = 'upload' | 'pairing' | 'cleaning' | 'quality';

// 文档状态
interface DocumentStatus {
  docId: string;
  fileName: string;
  fileType: 'question' | 'reply' | 'unknown';
  fileSize: string;
  uploadProgress: number;
  uploadStatus: 'pending' | 'uploading' | 'success' | 'error';
  pairingStatus: 'pending' | 'paired' | 'unpaired' | 'error';
  cleaningStatus: 'pending' | 'processing' | 'completed' | 'error';
  cleaningProgress?: number;
  qualityStatus: 'pending' | 'passed' | 'failed' | 'warning';
  qualityIssues?: string[];
  pairedDocId?: string;
}

// 项目数据
interface ProjectData {
  projectId: string;
  projectName: string;
  createTime: string;
  status: 'processing' | 'completed' | 'error';
  documents: DocumentStatus[];
}

// 模拟数据
const mockProjects: ProjectData[] = [
  {
    projectId: 'CASE-2024-0100',
    projectName: '云南烟叶复烤有限责任公司楚雄复烤厂智慧消防建设项目',
    createTime: '2024-02-22 10:30',
    status: 'processing',
    documents: [
      {
        docId: 'DOC-001-Q',
        fileName: '智慧消防项目质疑函.pdf',
        fileType: 'question',
        fileSize: '2.3MB',
        uploadProgress: 100,
        uploadStatus: 'success',
        pairingStatus: 'paired',
        cleaningStatus: 'completed',
        qualityStatus: 'passed',
        pairedDocId: 'DOC-001-R',
      },
      {
        docId: 'DOC-001-R',
        fileName: '智慧消防项目答复函.pdf',
        fileType: 'reply',
        fileSize: '2.1MB',
        uploadProgress: 100,
        uploadStatus: 'success',
        pairingStatus: 'paired',
        cleaningStatus: 'completed',
        qualityStatus: 'passed',
        pairedDocId: 'DOC-001-Q',
      },
    ],
  },
  {
    projectId: 'CASE-2024-0101',
    projectName: '数据中心建设项目',
    createTime: '2024-02-22 11:15',
    status: 'processing',
    documents: [
      {
        docId: 'DOC-002-Q',
        fileName: '数据中心项目质疑函.pdf',
        fileType: 'question',
        fileSize: '1.8MB',
        uploadProgress: 100,
        uploadStatus: 'success',
        pairingStatus: 'unpaired',
        cleaningStatus: 'pending',
        qualityStatus: 'pending',
      },
      {
        docId: 'DOC-002-R',
        fileName: '数据中心项目答复函.pdf',
        fileType: 'reply',
        fileSize: '1.9MB',
        uploadProgress: 100,
        uploadStatus: 'success',
        pairingStatus: 'unpaired',
        cleaningStatus: 'pending',
        qualityStatus: 'pending',
      },
    ],
  },
  {
    projectId: 'CASE-2024-0102',
    projectName: '办公用品采购项目',
    createTime: '2024-02-22 11:20',
    status: 'processing',
    documents: [
      {
        docId: 'DOC-003-Q',
        fileName: '办公用品采购质疑函.pdf',
        fileType: 'question',
        fileSize: '1.5MB',
        uploadProgress: 100,
        uploadStatus: 'success',
        pairingStatus: 'paired',
        cleaningStatus: 'processing',
        cleaningProgress: 65,
        qualityStatus: 'pending',
        pairedDocId: 'DOC-003-R',
      },
      {
        docId: 'DOC-003-R',
        fileName: '办公用品采购答复函.pdf',
        fileType: 'reply',
        fileSize: '1.4MB',
        uploadProgress: 100,
        uploadStatus: 'success',
        pairingStatus: 'paired',
        cleaningStatus: 'processing',
        cleaningProgress: 65,
        qualityStatus: 'pending',
        pairedDocId: 'DOC-003-Q',
      },
    ],
  },
  {
    projectId: 'CASE-2024-0103',
    projectName: '网络设备采购项目',
    createTime: '2024-02-22 11:25',
    status: 'error',
    documents: [
      {
        docId: 'DOC-004-Q',
        fileName: '网络设备采购质疑函.pdf',
        fileType: 'question',
        fileSize: '2.0MB',
        uploadProgress: 100,
        uploadStatus: 'success',
        pairingStatus: 'paired',
        cleaningStatus: 'completed',
        qualityStatus: 'failed',
        qualityIssues: ['页码不连续', 'OCR识别率低'],
        pairedDocId: 'DOC-004-R',
      },
      {
        docId: 'DOC-004-R',
        fileName: '网络设备采购答复函.pdf',
        fileType: 'reply',
        fileSize: '1.8MB',
        uploadProgress: 100,
        uploadStatus: 'success',
        pairingStatus: 'paired',
        cleaningStatus: 'completed',
        qualityStatus: 'warning',
        qualityIssues: ['部分文字识别模糊'],
        pairedDocId: 'DOC-004-Q',
      },
    ],
  },
];

// 处理步骤配置
const stepConfig = [
  {
    key: 'upload',
    title: '文件上传',
    icon: <CloudUploadOutlined />,
    description: '支持PDF/DOC/DOCX格式，单文件≤50MB',
    rules: ['文件格式正确', '文件大小合规', '上传完成'],
  },
  {
    key: 'pairing',
    title: '智能配对',
    icon: <SwapOutlined />,
    description: '自动匹配质疑函与答复函',
    rules: ['文件名相似度>80%', '文号匹配', '文件夹结构匹配'],
  },
  {
    key: 'cleaning',
    title: '清洗OCR',
    icon: <FileSyncOutlined />,
    description: '去除噪点、OCR识别、格式规范化',
    rules: ['去除页眉页脚', '敏感信息脱敏', 'OCR识别置信度>85%'],
  },
  {
    key: 'quality',
    title: '质量检查',
    icon: <SafetyCertificateOutlined />,
    description: '完整性校验、格式检查',
    rules: ['页码连续', '无乱码', '内容完整'],
  },
];

const DataUpload: React.FC = () => {
  const [projects, setProjects] = useState<ProjectData[]>(mockProjects);
  const [uploading, setUploading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectData | null>(null);
  const [pairingModalVisible, setPairingModalVisible] = useState(false);
  const [textEditModalVisible, setTextEditModalVisible] = useState(false);
  const [currentDoc, setCurrentDoc] = useState<DocumentStatus | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 统计数据
  const stats = {
    totalProjects: projects.length,
    uploading: projects.filter((p) => p.status === 'processing').length,
    ocrQueue: projects.flatMap((p) => p.documents).filter((d) => d.cleaningStatus === 'processing').length,
    qualityFailed: projects.flatMap((p) => p.documents).filter((d) => d.qualityStatus === 'failed').length,
    completed: projects.flatMap((p) => p.documents).filter((d) => d.qualityStatus === 'passed').length,
  };

  // 获取步骤状态
  const getStepStatus = (doc: DocumentStatus, stepIndex: number) => {
    const steps = ['upload', 'pairing', 'cleaning', 'quality'];
    const step = steps[stepIndex];

    switch (step) {
      case 'upload':
        if (doc.uploadStatus === 'error') return 'error';
        if (doc.uploadStatus === 'success') return 'finish';
        if (doc.uploadStatus === 'uploading') return 'process';
        return 'wait';
      case 'pairing':
        if (doc.pairingStatus === 'error') return 'error';
        if (doc.pairingStatus === 'paired') return 'finish';
        if (doc.uploadStatus !== 'success') return 'wait';
        return 'process';
      case 'cleaning':
        if (doc.cleaningStatus === 'error') return 'error';
        if (doc.cleaningStatus === 'completed') return 'finish';
        if (doc.cleaningStatus === 'processing') return 'process';
        if (doc.pairingStatus !== 'paired') return 'wait';
        return 'wait';
      case 'quality':
        if (doc.qualityStatus === 'failed') return 'error';
        if (doc.qualityStatus === 'passed') return 'finish';
        if (doc.qualityStatus === 'warning') return 'process';
        if (doc.cleaningStatus !== 'completed') return 'wait';
        return 'process';
      default:
        return 'wait';
    }
  };

  // 获取状态标签
  const getStatusTag = (status: string, type: string) => {
    const config: Record<string, Record<string, { color: string; text: string; icon?: React.ReactNode }>> = {
      upload: {
        pending: { color: 'default', text: '待上传' },
        uploading: { color: 'processing', text: '上传中', icon: <SyncOutlined spin /> },
        success: { color: 'success', text: '已上传', icon: <CheckCircleOutlined /> },
        error: { color: 'error', text: '上传失败', icon: <CloseOutlined /> },
      },
      pairing: {
        pending: { color: 'default', text: '待配对' },
        paired: { color: 'success', text: '已配对', icon: <LinkOutlined /> },
        unpaired: { color: 'warning', text: '未配对', icon: <WarningOutlined /> },
        error: { color: 'error', text: '配对失败', icon: <CloseOutlined /> },
      },
      cleaning: {
        pending: { color: 'default', text: '待清洗' },
        processing: { color: 'processing', text: '清洗中', icon: <SyncOutlined spin /> },
        completed: { color: 'success', text: '已完成', icon: <CheckCircleOutlined /> },
        error: { color: 'error', text: '清洗失败', icon: <CloseOutlined /> },
      },
      quality: {
        pending: { color: 'default', text: '待质检' },
        passed: { color: 'success', text: '通过', icon: <CheckCircleOutlined /> },
        failed: { color: 'error', text: '不合格', icon: <CloseOutlined /> },
        warning: { color: 'warning', text: '警告', icon: <WarningOutlined /> },
      },
    };

    const cfg = config[type]?.[status] || { color: 'default', text: status };
    return (
      <Tag color={cfg.color} icon={cfg.icon}>
        {cfg.text}
      </Tag>
    );
  };

  // 处理文件上传
  const handleUpload = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
    } else if (info.file.status === 'done') {
      message.success(`${info.file.name} 上传成功`);
      setUploading(false);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} 上传失败`);
      setUploading(false);
    }
  };

  // 处理文件夹选择
  const handleFolderSelect = () => {
    fileInputRef.current?.click();
  };

  // 打开配对干预弹窗
  const handlePairingIntervention = (project: ProjectData) => {
    setSelectedProject(project);
    setPairingModalVisible(true);
  };

  // 打开文本修正弹窗
  const handleTextEdit = (doc: DocumentStatus) => {
    setCurrentDoc(doc);
    setTextEditModalVisible(true);
  };

  // 开始标注
  const handleStartAnnotation = (projectId: string) => {
    message.success(`进入标注工作台: ${projectId}`);
    // navigate(`/annotation/${projectId}`);
  };

  // 重新处理
  const handleReprocess = (docId: string) => {
    message.info(`重新处理文档: ${docId}`);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">数据上传清洗</h1>

      {/* ========== 1. 微型统计数据看板 ========== */}
      <Row gutter={16} className="mb-6">
        <Col span={4}>
          <Card>
            <Statistic title="总项目数" value={stats.totalProjects} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="上传中/处理中"
              value={stats.uploading}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CloudUploadOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="OCR排队/处理中"
              value={stats.ocrQueue}
              valueStyle={{ color: '#faad14' }}
              prefix={<FileSyncOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="质检不合格"
              value={stats.qualityFailed}
              valueStyle={{ color: '#f5222d' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="已完成"
              value={stats.completed}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* ========== 2. 文件上传区 ========== */}
      <Card title="📤 文件上传" className="mb-6">
        <div className="flex items-center gap-4">
          <Upload
            multiple
            directory={false}
            customRequest={({ onSuccess }) => setTimeout(() => onSuccess?.('ok'), 500)}
            onChange={handleUpload}
            showUploadList={false}
          >
            <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
              批量上传文件
            </Button>
          </Upload>

          <Button icon={<FolderOpenOutlined />} onClick={handleFolderSelect}>
            选择文件夹批量扫描
          </Button>

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            // @ts-ignore
            webkitdirectory=""
            directory=""
            multiple
            onChange={(e) => {
              const files = e.target.files;
              if (files && files.length > 0) {
                message.success(`已选择 ${files.length} 个文件`);
              }
            }}
          />

          <div className="text-gray-400 text-sm">
            支持 PDF、DOC、DOCX 格式，单文件 ≤ 50MB
          </div>
        </div>
      </Card>

      {/* ========== 3. 文件处理区（处理流程环节展示） ========== */}
      <Card title="⚙️ 文件处理流程" className="mb-6">
        <div className="mb-4 p-4 bg-blue-50 rounded">
          <div className="font-medium mb-2">处理流程说明</div>
          <Steps current={-1}>
            {stepConfig.map((step) => (
              <Step
                key={step.key}
                title={step.title}
                icon={step.icon}
                description={step.description}
              />
            ))}
          </Steps>
        </div>

        {/* 项目列表 - 显示处理流程 */}
        <Collapse defaultActiveKey={['CASE-2024-0100']}>
          {projects.map((project) => (
            <Panel
              key={project.projectId}
              header={
                <div className="flex items-center justify-between w-full pr-8">
                  <div className="flex items-center gap-4">
                    <span className="font-medium">{project.projectId}</span>
                    <span className="text-gray-600">{project.projectName}</span>
                    <Tag
                      color={
                        project.status === 'completed'
                          ? 'success'
                          : project.status === 'error'
                          ? 'error'
                          : 'processing'
                      }
                    >
                      {project.status === 'completed'
                        ? '已完成'
                        : project.status === 'error'
                        ? '异常'
                        : '处理中'}
                    </Tag>
                  </div>
                  <span className="text-gray-400 text-sm">{project.createTime}</span>
                </div>
              }
            >
              {/* 项目下的文档处理流程 */}
              <div className="space-y-4">
                {project.documents.map((doc) => (
                  <Card key={doc.docId} className="bg-gray-50">
                    <div className="grid grid-cols-[1fr_2fr] gap-4">
                      {/* 左侧：文档信息 */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FileTextOutlined className="text-blue-500" />
                          <span className="font-medium">{doc.fileName}</span>
                          <Tag color={doc.fileType === 'question' ? 'blue' : 'green'}>
                            {doc.fileType === 'question' ? '质疑函' : '答复函'}
                          </Tag>
                        </div>
                        <div className="text-sm text-gray-500">
                          大小: {doc.fileSize} | ID: {doc.docId}
                        </div>
                        {doc.pairedDocId && (
                          <div className="text-sm text-green-600 mt-1">
                            <LinkOutlined /> 已配对: {doc.pairedDocId}
                          </div>
                        )}
                      </div>

                      {/* 右侧：处理流程步骤 */}
                      <div>
                        <Steps current={3}>
                          <Step
                            status={getStepStatus(doc, 0)}
                            title="上传"
                            icon={<CloudUploadOutlined />}
                            description={
                              doc.uploadStatus === 'uploading' ? (
                                <Progress percent={doc.uploadProgress} />
                              ) : (
                                getStatusTag(doc.uploadStatus, 'upload')
                              )
                            }
                          />
                          <Step
                            status={getStepStatus(doc, 1)}
                            title="配对"
                            icon={<SwapOutlined />}
                            description={getStatusTag(doc.pairingStatus, 'pairing')}
                          />
                          <Step
                            status={getStepStatus(doc, 2)}
                            title="清洗OCR"
                            icon={<FileSyncOutlined />}
                            description={
                              doc.cleaningStatus === 'processing' ? (
                                <Progress percent={doc.cleaningProgress || 0} status="active" />
                              ) : (
                                getStatusTag(doc.cleaningStatus, 'cleaning')
                              )
                            }
                          />
                          <Step
                            status={getStepStatus(doc, 3)}
                            title="质检"
                            icon={<SafetyCertificateOutlined />}
                            description={getStatusTag(doc.qualityStatus, 'quality')}
                          />
                        </Steps>

                        {/* 质检问题显示 */}
                        {doc.qualityIssues && doc.qualityIssues.length > 0 && (
                          <Alert
                            message="质检问题"
                            description={
                              <ul className="list-disc list-inside">
                                {doc.qualityIssues.map((issue, idx) => (
                                  <li key={idx}>{issue}</li>
                                ))}
                              </ul>
                            }
                            type="error"
                           
                            className="mt-2"
                            showIcon
                          />
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* 项目级操作 */}
              <div className="mt-4 flex gap-2">
                {project.documents.some((d) => d.pairingStatus === 'unpaired') && (
                  <Button type="primary" icon={<SwapOutlined />} onClick={() => handlePairingIntervention(project)}>
                    配对干预
                  </Button>
                )}
                {project.documents.some((d) => d.qualityStatus === 'failed' || d.qualityStatus === 'warning') && (
                  <Button icon={<EditOutlined />} onClick={() => handleTextEdit(project.documents[0])}>
                    文本修正
                  </Button>
                )}
                {project.documents.every((d) => d.qualityStatus === 'passed') && (
                  <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => handleStartAnnotation(project.projectId)}>
                    开始标注
                  </Button>
                )}
              </div>
            </Panel>
          ))}
        </Collapse>
      </Card>

      {/* ========== 4. 成果区 ========== */}
      <Card
        title="📊 处理成果"
        extra={
          <Space>
            <Tag color="success">通过: {stats.completed}</Tag>
            <Tag color="error">不合格: {stats.qualityFailed}</Tag>
          </Space>
        }
      >
        <Table
          dataSource={projects.flatMap((p) =>
            p.documents.map((d) => ({
              ...d,
              projectId: p.projectId,
              projectName: p.projectName,
            }))
          )}
          rowKey="docId"
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: '项目ID',
              dataIndex: 'projectId',
              width: 150,
            },
            {
              title: '项目名称',
              dataIndex: 'projectName',
              ellipsis: true,
            },
            {
              title: '文档名称',
              dataIndex: 'fileName',
            },
            {
              title: '类型',
              dataIndex: 'fileType',
              width: 100,
              render: (type) => (
                <Tag color={type === 'question' ? 'blue' : 'green'}>
                  {type === 'question' ? '质疑函' : '答复函'}
                </Tag>
              ),
            },
            {
              title: '上传',
              dataIndex: 'uploadStatus',
              width: 100,
              render: (status) => getStatusTag(status, 'upload'),
            },
            {
              title: '配对',
              dataIndex: 'pairingStatus',
              width: 100,
              render: (status) => getStatusTag(status, 'pairing'),
            },
            {
              title: '清洗OCR',
              dataIndex: 'cleaningStatus',
              width: 120,
              render: (status, record) =>
                status === 'processing' ? (
                  <Progress percent={record.cleaningProgress || 0} status="active" />
                ) : (
                  getStatusTag(status, 'cleaning')
                ),
            },
            {
              title: '质检',
              dataIndex: 'qualityStatus',
              width: 100,
              render: (status, record) => (
                <div>
                  {getStatusTag(status, 'quality')}
                  {record.qualityIssues && (
                    <Tooltip title={record.qualityIssues.join(', ')}>
                      <WarningOutlined className="text-red-500 ml-1" />
                    </Tooltip>
                  )}
                </div>
              ),
            },
            {
              title: '操作',
              key: 'action',
              width: 200,
              render: (_, record) => (
                <Space>
                  {record.pairingStatus === 'unpaired' && (
                    <Button type="primary" icon={<SwapOutlined />}>
                      配对
                    </Button>
                  )}
                  {(record.qualityStatus === 'failed' || record.qualityStatus === 'warning') && (
                    <Button icon={<EditOutlined />} onClick={() => handleTextEdit(record)}>
                      修正
                    </Button>
                  )}
                  {record.qualityStatus === 'passed' && (
                    <Button type="primary" icon={<PlayCircleOutlined />}>
                      标注
                    </Button>
                  )}
                  <Button icon={<ReloadOutlined />} onClick={() => handleReprocess(record.docId)}>
                    重试
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      {/* 配对干预弹窗 */}
      <Modal
        title="配对干预"
        open={pairingModalVisible}
        onCancel={() => setPairingModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setPairingModalVisible(false)}>
            取消
          </Button>,
          <Button key="confirm" type="primary">
            确认配对
          </Button>,
        ]}
      >
        {selectedProject && (
          <div>
            <Alert
              message="手动配对"
              description="选择质疑函和对应的答复函进行配对"
              type="info"
              className="mb-4"
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="font-medium mb-2">质疑函</div>
                {selectedProject.documents
                  .filter((d) => d.fileType === 'question')
                  .map((doc) => (
                    <Card key={doc.docId} className="mb-2">
                      {doc.fileName}
                    </Card>
                  ))}
              </div>
              <div>
                <div className="font-medium mb-2">答复函</div>
                {selectedProject.documents
                  .filter((d) => d.fileType === 'reply')
                  .map((doc) => (
                    <Card key={doc.docId} className="mb-2">
                      {doc.fileName}
                    </Card>
                  ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 文本修正弹窗 */}
      <Modal
        title="文本修正"
        open={textEditModalVisible}
        onCancel={() => setTextEditModalVisible(false)}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setTextEditModalVisible(false)}>
            取消
          </Button>,
          <Button key="save" type="primary">
            保存修正
          </Button>,
        ]}
      >
        {currentDoc && (
          <div>
            <Alert
              message="OCR文本修正"
              description="请校对并修正OCR识别错误的文本内容"
              type="warning"
              className="mb-4"
            />
            <Descriptions className="mb-4">
              <Descriptions.Item label="文档">{currentDoc.fileName}</Descriptions.Item>
              <Descriptions.Item label="状态">{getStatusTag(currentDoc.qualityStatus, 'quality')}</Descriptions.Item>
            </Descriptions>
            {currentDoc.qualityIssues && (
              <Alert
                message="检测到的问题"
                description={currentDoc.qualityIssues.join(', ')}
                type="error"
                className="mb-4"
              />
            )}
            <TextArea rows={15} placeholder="OCR识别文本将显示在这里..." />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DataUpload;
